import { useEffect, useRef } from "react";
import p5 from "p5";

export default function ParticleBackground() {
    const containerRef = useRef(null);

    useEffect(() => {
        if (!containerRef.current) return;

        const sketch = (p) => {
            const particles = [];
            const particleCount = p.windowWidth < 768 ? 50 : 100;
            let mouseX = p.windowWidth / 2;
            let mouseY = p.windowHeight / 2;
            let time = 0;

            class Particle {
                constructor() {
                    this.pos = p.createVector(p.random(p.width), p.random(p.height));
                    this.vel = p.createVector(p.random(-0.5, 0.5), p.random(-0.5, 0.5));
                    this.acc = p.createVector();
                    this.size = p.random(2, 5);
                    this.maxSpeed = p.random(1, 3);
                    this.color = [p.random(0, 50), p.random(0, 50), p.random(0, 50)];
                    this.shape = Math.floor(p.random(4));
                }

                update() {
                    // Create attraction to mouse
                    const mouse = p.createVector(mouseX, mouseY);
                    const dir = p5.Vector.sub(mouse, this.pos);
                    const distance = dir.mag();

                    // Set magnitude based on distance
                    const strength = p.constrain(50 / (distance * distance), 0, 0.7);
                    dir.normalize();
                    dir.mult(strength);

                    // Apply force
                    this.acc.add(dir);

                    // Update velocity and position
                    this.vel.add(this.acc);
                    this.vel.limit(this.maxSpeed);
                    this.pos.add(this.vel);
                    this.acc.mult(0);

                    // Wrap around edges
                    if (this.pos.x > p.width) this.pos.x = 0;
                    if (this.pos.x < 0) this.pos.x = p.width;
                    if (this.pos.y > p.height) this.pos.y = 0;
                    if (this.pos.y < 0) this.pos.y = p.height;
                }

                draw() {
                    // Calculate opacity based on distance to mouse
                    const mouse = p.createVector(mouseX, mouseY);
                    const distance = p.dist(this.pos.x, this.pos.y, mouse.x, mouse.y);
                    const opacity = p.map(distance, 0, 300, 255, 50);
                    const size = this.size * (1 + p.map(distance, 0, 100, 1, 0) * 2);

                    p.push();
                    p.translate(this.pos.x, this.pos.y);
                    p.rotate(time * 0.5);
                    p.noStroke();
                    p.fill(this.color[0], this.color[1], this.color[2], opacity);

                    // Draw different geometric shapes
                    switch (this.shape) {
                        case 0: // Circle
                            p.ellipse(0, 0, size);
                            break;
                        case 1: // Square
                            p.rectMode(p.CENTER);
                            p.rect(0, 0, size, size);
                            break;
                        case 2: // Triangle
                            p.triangle(0, -size / 2, size / 2, size / 2, -size / 2, size / 2);
                            break;
                        case 3: // Diamond
                            p.quad(0, -size / 2, size / 2, 0, 0, size / 2, -size / 2, 0);
                            break;
                        default: // Default to circle
                            p.ellipse(0, 0, size);
                            break;
                    }
                    p.pop();
                }

                connect(particles) {
                    particles.forEach((other) => {
                        const d = p.dist(this.pos.x, this.pos.y, other.pos.x, other.pos.y);
                        if (d < 150 && d > 0) {
                            const alpha = p.map(d, 0, 150, 100, 0);

                            // Draw line with gradient
                            p.strokeWeight(p.map(d, 0, 150, 1.5, 0.1));
                            p.stroke(
                                p.lerp(this.color[0], other.color[0], 0.5),
                                p.lerp(this.color[1], other.color[1], 0.5),
                                p.lerp(this.color[2], other.color[2], 0.5),
                                alpha
                            );
                            p.line(this.pos.x, this.pos.y, other.pos.x, other.pos.y);

                            // Sometimes draw geometric shapes at midpoints
                            if (d < 100 && p.random() < 0.05) {
                                const midX = (this.pos.x + other.pos.x) / 2;
                                const midY = (this.pos.y + other.pos.y) / 2;
                                const midSize = p.map(d, 0, 100, 8, 3);

                                p.push();
                                p.translate(midX, midY);
                                p.noStroke();
                                p.fill(
                                    p.lerp(this.color[0], other.color[0], 0.5),
                                    p.lerp(this.color[1], other.color[1], 0.5),
                                    p.lerp(this.color[2], other.color[2], 0.5),
                                    alpha * 1.5
                                );

                                const shapeType = Math.floor(p.random(3));
                                switch (shapeType) {
                                    case 0:
                                        p.ellipse(0, 0, midSize);
                                        break;
                                    case 1:
                                        p.rectMode(p.CENTER);
                                        p.rect(0, 0, midSize, midSize);
                                        break;
                                    case 2:
                                        p.triangle(
                                            0,
                                            -midSize / 2,
                                            midSize / 2,
                                            midSize / 2,
                                            -midSize / 2,
                                            midSize / 2
                                        );
                                        break;
                                    default:
                                        p.ellipse(0, 0, midSize);
                                        break;
                                }
                                p.pop();
                            }
                        }
                    });
                }
            }

            p.setup = () => {
                const canvas = p.createCanvas(p.windowWidth, p.windowHeight);
                canvas.position(0, 0);
                canvas.style("z-index", "-1");

                // Create particles
                for (let i = 0; i < particleCount; i++) {
                    particles.push(new Particle());
                }
            };

            p.draw = () => {
                p.clear();
                time += 0.01;

                // Draw triangulation between close particles
                p.noStroke();
                for (let i = 0; i < particles.length - 2; i += 3) {
                    const p1 = particles[i];
                    const p2 = particles[i + 1];
                    const p3 = particles[i + 2];

                    const d1 = p.dist(p1.pos.x, p1.pos.y, p2.pos.x, p2.pos.y);
                    const d2 = p.dist(p2.pos.x, p2.pos.y, p3.pos.x, p3.pos.y);
                    const d3 = p.dist(p3.pos.x, p3.pos.y, p1.pos.x, p1.pos.y);

                    const avgDist = (d1 + d2 + d3) / 3;

                    if (avgDist < 150) {
                        const alpha = p.map(avgDist, 0, 150, 30, 0);
                        p.fill(
                            (p1.color[0] + p2.color[0] + p3.color[0]) / 3,
                            (p1.color[1] + p2.color[1] + p3.color[1]) / 3,
                            (p1.color[2] + p2.color[2] + p3.color[2]) / 3,
                            alpha
                        );
                        p.triangle(
                            p1.pos.x,
                            p1.pos.y,
                            p2.pos.x,
                            p2.pos.y,
                            p3.pos.x,
                            p3.pos.y
                        );
                    }
                }

                // Update and draw particles
                for (let i = 0; i < particles.length; i++) {
                    particles[i].update();
                    particles[i].draw();

                    // Only connect every other particle to improve performance
                    if (i % 2 === 0) {
                        particles[i].connect(particles);
                    }
                }
            };

            p.mouseMoved = () => {
                mouseX = p.mouseX;
                mouseY = p.mouseY;
            };

            p.touchMoved = () => {
                if (p.touches.length > 0) {
                    mouseX = p.touches[0].x || p.windowWidth / 2;
                    mouseY = p.touches[0].y || p.windowHeight / 2;
                }
                return false; // Prevent default
            };

            p.windowResized = () => {
                p.resizeCanvas(p.windowWidth, p.windowHeight);
            };
        };

        const p5Instance = new p5(sketch, containerRef.current);

        return () => {
            p5Instance.remove();
        };
    }, []);

    return (
        <div
            ref={containerRef}
            className="fixed top-0 left-0 w-full h-full -z-10"
        />
    );
}
